<template>
    <div class="wrapper w-100 h-100 d-flex justify-center align-center">
        <v-card class="login-card" elevation="6" rounded="xl">
            <v-card-text class="pa-8 pa-sm-10">
                <!-- Logo -->
                <div class="text-center mb-6">
                    <router-link class="d-inline-flex align-center text-decoration-none" to="/">
                        <v-img :src="logoFull" :width="200" draggable="false"/>
                        <span class="font-weight-bold font-italic text-h4 text-primary ms-1">Go</span>
                    </router-link>
                </div>

                <h2 class="text-h5 font-weight-bold text-center mb-1">
                    Let's verify it's you
                </h2>
                <p class="text-body-2 text-medium-emphasis text-center mb-6">
                    We'll text you a 6-digit code so you know your account is really yours.
                </p>

                <v-form v-model="formValid" validate-on="input lazy"
                        @submit.prevent="onSendCode">
                    <div class="d-flex ga-2">
                        <v-autocomplete v-model="country"
                                        :items="countryCodes"
                                        :rules="[rules.required]"
                                        item-title="label"
                                        item-value="iso"
                                        return-object
                                        density="compact"
                                        color="primary"
                                        label="Country"
                                        style="max-width: 160px"
                                        :disabled="submitting"
                                        hide-details="auto">
                            <template #selection="{item}">
                                <span class="me-1">{{ item.raw.flag }}</span>
                                <span class="font-weight-medium">{{ item.raw.dial }}</span>
                            </template>
                            <template #item="{item, props}">
                                <v-list-item v-bind="props" :title="item.raw.label">
                                    <template #prepend>
                                        <span class="me-2" style="font-size: 18px">{{ item.raw.flag }}</span>
                                    </template>
                                    <template #append>
                                        <span class="text-caption text-medium-emphasis">{{ item.raw.dial }}</span>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <v-text-field v-model="phoneLocal"
                                      :rules="[rules.required, rules.phoneLocal]"
                                      label="Mobile phone"
                                      placeholder="(212) 555-1234"
                                      prepend-inner-icon="mdi-cellphone"
                                      type="tel"
                                      color="primary"
                                      density="compact"
                                      autocomplete="tel"
                                      inputmode="tel"
                                      :disabled="submitting"
                                      hide-details="auto"
                                      class="flex-grow-1"/>
                    </div>

                    <p class="text-caption text-medium-emphasis mt-1 mb-2">
                        We'll text a 6-digit code to <strong>{{ country?.flag }} {{ country?.dial }}</strong>.
                    </p>

                    <!-- Turnstile - only mounted when the server says it's required. -->
                    <div v-if="captchaEnabled && captchaSiteKey" class="d-flex justify-center my-4">
                        <TurnstileWidget :site-key="captchaSiteKey"
                                         @token="onCaptchaToken"
                                         @error="onCaptchaError"
                                         @expired="onCaptchaExpired"/>
                    </div>

                    <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4" density="compact">
                        {{ errorMessage }}
                    </v-alert>

                    <v-btn type="submit"
                           block color="primary" size="large"
                           class="mt-2"
                           :loading="submitting"
                           :disabled="!canSubmit">
                        <template v-if="throttleIn > 0">Try again in {{ throttleIn }}s</template>
                        <template v-else>Send verification code</template>
                    </v-btn>

                    <div class="text-center mt-6">
                        <span class="text-body-2 text-medium-emphasis">Already have an account?</span>
                        <router-link to="/login" class="text-body-2 ml-1 text-primary text-decoration-none font-weight-medium">
                            Sign in
                        </router-link>
                    </div>
                </v-form>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {apiErrorMessage} from "@/composables/useApiError";
import {useSnackbarStore} from "@/stores/snackBarStore";
import TurnstileWidget from "@/components/auth/TurnstileWidget.vue";
import {COUNTRY_PHONE_CODES, DEFAULT_COUNTRY} from "@/assets/js/country-phone-codes";
import logoFull from "@/assets/images/logo-full.png";

const router = useRouter();
const snackBar = useSnackbarStore();

const formValid = ref(false);
const phoneLocal = ref("");
const submitting = ref(false);
const errorMessage = ref("");
const throttleIn = ref(0);
let throttleTimer = null;

// Country list shared with fleet web; default US to match the server's
// primary allowlist entry.
const countryCodes = COUNTRY_PHONE_CODES;
const country = ref(DEFAULT_COUNTRY);

const startThrottleCooldown = (seconds) => {
    const clamped = Math.max(1, Math.min(600, Math.floor(seconds || 60)));
    throttleIn.value = clamped;
    if (throttleTimer) clearInterval(throttleTimer);
    throttleTimer = setInterval(() => {
        throttleIn.value--;
        if (throttleIn.value <= 0) clearInterval(throttleTimer);
    }, 1000);
};

onBeforeUnmount(() => { if (throttleTimer) clearInterval(throttleTimer); });

const captchaEnabled = ref(false);
const captchaSiteKey = ref("");
const captchaToken = ref("");

const rules = {
    required: (v) => (v != null && String(v).trim() !== "") || "Required",
    phoneLocal: (v) => {
        const digits = String(v || "").replace(/\D/g, "");
        return digits.length >= 4 || "Enter a valid phone number.";
    },
};

// Assemble the E.164 payload the server expects: country dial code +
// local digits, stripping any separators the user typed. If a user
// pasted the full number including their country code into the local
// field (browser autofill frequently does this), detect and strip the
// matching dial prefix so we don't double it up.
const fullPhone = computed(() => {
    const dial = country.value?.dial ?? "";
    let digits = String(phoneLocal.value || "").replace(/\D/g, "");
    if (!digits) return "";
    const dialDigits = dial.replace(/\D/g, "");
    if (dialDigits && digits.startsWith(dialDigits) && digits.length > dialDigits.length) {
        digits = digits.slice(dialDigits.length);
    }
    return `${dial}${digits}`;
});

// Compute canSubmit from raw field values. Vuetify's validate-on="input
// lazy" doesn't flip formValid to true on initial mount for pre-filled
// fields, so the US-default country + a typed phone number didn't
// enable the submit button until the user refocused something.
const canSubmit = computed(() => {
    if (submitting.value || throttleIn.value > 0) return false;
    if (!country.value?.dial) return false;
    const digits = String(phoneLocal.value || "").replace(/\D/g, "");
    if (digits.length < 4) return false;
    if (captchaEnabled.value && captchaSiteKey.value && !captchaToken.value) return false;
    return true;
});

onMounted(async () => {
    // Discover CAPTCHA state. Failure → assume disabled (server will 403
    // if it actually requires a token, which we'll surface as an error).
    try {
        const res = await send("get", "auth/captcha-config");
        const data = res?.data?.data ?? {};
        captchaEnabled.value = !!data.enabled;
        captchaSiteKey.value = data.site_key || "";
    } catch (_) {
        captchaEnabled.value = false;
        captchaSiteKey.value = "";
    }
});

function onCaptchaToken(tok) {
    captchaToken.value = tok || "";
}
function onCaptchaError() {
    captchaToken.value = "";
}
function onCaptchaExpired() {
    captchaToken.value = "";
}

async function onSendCode() {
    if (!canSubmit.value) return;

    submitting.value = true;
    errorMessage.value = "";

    const payload = {phone: fullPhone.value};
    if (captchaToken.value) payload.captcha_token = captchaToken.value;

    try {
        const res = await send("post", "auth/phone/send-otp", payload);
        if (res?.data?.success) {
            snackBar.showSnackbar("Verification code sent.", "success");
            router.push({
                name: "phone-otp",
                query: {phone: payload.phone},
            });
        } else {
            errorMessage.value = res?.data?.message || "Could not send verification code.";
        }
    } catch (e) {
        const info = apiErrorMessage(e, "Could not send verification code. Please try again.");
        errorMessage.value = info.message;
        if (info.isThrottled) {
            startThrottleCooldown(info.retryAfterSec || 60);
        }
    } finally {
        submitting.value = false;
    }
}
</script>

<style scoped>
.wrapper {
    min-height: 100vh;
}
.login-card {
    width: 100%;
    max-width: 440px;
}
</style>
